import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import { Subject } from "rxjs";
import SwiperCore, { Navigation, Pagination, SwiperOptions } from "swiper";
import { SwiperComponent } from "swiper/angular";
import * as uuid from "uuid";

import { MediaQueryOption } from "@hermes/aphrodite/media-picture";
import {
    CTALink,
    GalleryCarouselDeprecated,
    Media,
    TOKEN_TYPE,
} from "@hermes/api-model-editorial";
import { Context } from "@hermes/app-core";

import { SHOULD_PRELOAD_MEDIA, CHAPTERS_PARAGRAPH } from "../../core";

import { GalleryCarouselAnalyticsService } from "./gallery-carousel-analytics.service";

SwiperCore.use([Navigation, Pagination]);

const NUMBER_OF_SLIDES_TO_LOAD = 4;

@Component({
    selector: "h-gallery-carousel",
    templateUrl: "./gallery-carousel.component.html",
    styleUrls: ["./gallery-carousel.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    // Mandatory because of Swiper external library
    // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class GalleryCarouselComponent implements OnInit {
    @ViewChild("swiper")
    public swiper!: SwiperComponent;
    public activeIndex = 0;
    public paginationId: string = uuid.v4();
    public paginationClass: string = `swiper-pagination swiper-pagination-${this.paginationId}`;
    public mediaOptions = galleryCarouselPosterOptions;
    public pauseEventSubjects: Array<Subject<void>> = Array.from({
        length: this.galleryCarousel.mediaSlides.length,
    }).map(() => new Subject<void>());
    public sliderConfig: SwiperOptions = {
        // Default config
        loop: true,
        slidesPerView: 1.25,
        spaceBetween: 20,
        simulateTouch: true,
        updateOnWindowResize: true,
        updateOnImagesReady: true,
        pagination: {
            el: `.swiper-pagination-${this.paginationId}`,
            type: "custom",
            // Swiper parameter is requested for configuration
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            renderCustom: (swiper, current, total): string =>
                `${pad(current, 2)} / ${pad(total, 2)}`,
        },
        centeredSlides: true,
        centeredSlidesBounds: true,
        loopAdditionalSlides: 100,
        /**
         * Fix the Issue when right-clicking on a slide
         * then nothing should happen
         */
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        preventClicks: true,
        preventClicksPropagation: true,
        breakpoints: {
            // when window width is >= 768px
            // We must use a number as key because of the Swiper external library
            // eslint-disable-next-line @typescript-eslint/naming-convention
            768: {
                slidesPerView: 3.4,
                spaceBetween: 20,
            },
            // when window width is >= 1024px
            // We must use a number as key because of the Swiper external library
            // eslint-disable-next-line @typescript-eslint/naming-convention
            1024: {
                slidesPerView: 3.4,
                spaceBetween: 40,
            },
        },
    };

    constructor(
        @Inject(CHAPTERS_PARAGRAPH)
        public galleryCarousel: GalleryCarouselDeprecated,
        @Inject(SHOULD_PRELOAD_MEDIA) public shouldPreloadMedia: boolean,
        public cdr: ChangeDetectorRef,
        private galleryCarouselAnalyticsService: GalleryCarouselAnalyticsService,
        private context: Context,
    ) {}

    public ngOnInit(): void {
        this.filterOutEmptyMedias();
    }

    public isInBrowserMode(): boolean {
        return this.context.isInBrowserMode();
    }

    public filterOutEmptyMedias(): void {
        for (const [
            index,
            mediaSlide,
        ] of this.galleryCarousel.mediaSlides.entries()) {
            if (!mediaSlide.media) {
                this.galleryCarousel.mediaSlides.splice(index, 1);
            }
        }
    }

    public previousSlide(): void {
        this.swiper?.swiperRef.slidePrev();
    }

    public nextSlide(): void {
        this.swiper?.swiperRef.slideNext();
    }

    public onMediaRedirectionEvent(ctaClicked: CTALink, caption: string) {
        if (ctaClicked.tokenType === TOKEN_TYPE.ctaCategory) {
            this.galleryCarouselAnalyticsService.sendUAEvent({
                eventaction: `${this.activeIndex + 1} | ${caption || ""}`,
                eventlabel: ctaClicked.path,
            });
        }
    }

    public changeSlide() {
        if (this.swiper?.swiperRef?.realIndex !== undefined) {
            // Event are outside of NgZone so we need to manually detect changes
            this.cdr.detectChanges();
            // If we swipe out a video we pause it
            if (
                this.galleryCarousel.mediaSlides[this.activeIndex].media
                    ?.videoUrl
            ) {
                this.pauseEventSubjects[this.activeIndex].next();
            }
            this.activeIndex = this.swiper.swiperRef.realIndex;
        }
    }

    // Should not be autoplay if not centered slide
    public setMediaAutoplay(media: Media, index: number): Media {
        if (this.activeIndex !== index && media.videoUrl) {
            return {
                ...media,
                videoLoopMuteAutoplay: false,
            };
        }
        return media;
    }

    // We only want to preload current slide + 4 slides on the left and 4 slides on the right
    // We also want to load the 4 slides on the left on initial state (activeIndex = 0, load slides with indexes 20 to 24 and 0 to 3)
    public isMediaPreloaded(index: number): boolean {
        return (
            (index <= this.activeIndex + NUMBER_OF_SLIDES_TO_LOAD &&
                index >= this.activeIndex - NUMBER_OF_SLIDES_TO_LOAD) ||
            index >
                this.activeIndex +
                    this.galleryCarousel.mediaSlides.length -
                    NUMBER_OF_SLIDES_TO_LOAD ||
            index <
                this.activeIndex -
                    this.galleryCarousel.mediaSlides.length +
                    NUMBER_OF_SLIDES_TO_LOAD
        );
    }
}

export const pad = (numberToPad: number, size: number): string =>
    `000000000${numberToPad}`.slice(-size);

const galleryCarouselPosterOptions: MediaQueryOption[] = [
    {
        size: "xsmall",
        mediaQuery: "(min-width: 320px) and (max-width: 414px)",
        width: 414,
    },
    {
        size: "small",
        mediaQuery: "(min-width: 415px) and (max-width: 768px)",
        width: 768,
    },
    {
        size: "medium",
        mediaQuery: "(min-width: 769px) and (max-width: 1024px)",
        width: 308,
    },
    {
        size: "large",
        mediaQuery: "(min-width: 1025px) and (max-width: 1280px)",
        width: 414,
    },
    {
        size: "xlarge",
        mediaQuery: "(min-width: 1281px) and (max-width: 1920px)",
        width: 768,
    },
    { size: "xxlarge", mediaQuery: "(min-width: 1921px)", width: 768 },
];
